/**
 * Toggle Button Component
 *
 * Button component for toggle/selection states (e.g., filter buttons, tab-like selections).
 * Automatically handles active/inactive states with proper styling.
 *
 * Usage:
 *   <ToggleButton isActive={selected === 'option1'} onClick={() => setSelected('option1')}>
 *     Option 1
 *   </ToggleButton>
 *
 *   <ToggleButton isActive={filters.sort === 'new'} onClick={() => setFilters({...filters, sort: 'new'})} startIcon={<Clock />}>
 *     New
 *   </ToggleButton>
 */

import * as React from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

interface ToggleButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Whether the button is in active/selected state
   */
  isActive?: boolean;
  /**
   * Variant to use when active (default: 'default')
   */
  activeVariant?: ButtonProps['variant'];
  /**
   * Variant to use when inactive (default: 'outline')
   */
  inactiveVariant?: ButtonProps['variant'];
  /**
   * Additional className when active
   */
  activeClassName?: string;
  /**
   * Additional className when inactive
   */
  inactiveClassName?: string;
}

/**
 * ToggleButton - Button for toggle/selection states
 * Used for: Filter buttons, tab-like selections, option groups
 */
export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      className,
      isActive = false,
      activeVariant = 'default',
      inactiveVariant = 'outline',
      activeClassName,
      inactiveClassName,
      type = 'button', // Default to 'button' to prevent form submission
      ...props
    },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        variant={isActive ? activeVariant : inactiveVariant}
        className={cn(
          isActive ? activeClassName : inactiveClassName,
          className,
        )}
        aria-pressed={isActive}
        type={type}
        {...props}
      >
        {props.children}
      </Button>
    );
  },
);
ToggleButton.displayName = 'ToggleButton';
